import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSupabase, isSupabaseConfigured } from '@/lib/supabase';
import { isLang, type Lang } from '@/types/lang';

/**
 * useUserProfile — fetch the public.users row for the signed-in user.
 *
 * Returns:
 *   - { profile: row | null, loading: false } once resolved
 *   - profile === null when no row exists yet (user has authenticated via OTP
 *     but hasn't completed Onboarding) — App router uses this to show
 *     <Onboarding> after sign-in.
 *   - loading === true during the initial fetch (and on session changes).
 *
 * Re-fetches when the session id changes (sign-out → sign-in flow).
 *
 * Used by the App router (App.tsx) to decide between Onboarding and Home.
 */

export interface UserProfile {
  id: string;
  name: string;
  role_label: string;
  /** Oturum 8: per-UI-lang translations of role_label, filled by Edge Functions
   *  via Anthropic fanout. Reader picks the current lang or falls back to
   *  role_label (the value the user typed in their own native language). */
  role_label_translations: Record<string, string> | null;
  ui_lang: Lang;
  native_lang: Lang;
  timezone: string;
  family_side: 'tr' | 'bs' | null;
}

export interface ProfileState {
  profile: UserProfile | null;
  loading: boolean;
  /** Bump to force a re-fetch (after onboarding completes). */
  refresh: () => void;
}

export function useUserProfile(session: Session | null): ProfileState {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      setProfile(null);
      return;
    }

    if (!session?.user?.id) {
      setLoading(false);
      setProfile(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('users')
        .select(
          'id, name, role_label, role_label_translations, ui_lang, native_lang, timezone, family_side',
        )
        .eq('id', session.user.id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        // PGRST116 = "no rows" → expected for not-yet-onboarded users; treat as null.
        // Other errors → log + treat as null (App will show Onboarding; Edge Function
        // upsert is idempotent so user can retry).
        console.error('useUserProfile fetch error:', error.message);
        setProfile(null);
      } else if (data && isLang(data.ui_lang) && isLang(data.native_lang)) {
        const rawTranslations: unknown = data.role_label_translations;
        const role_label_translations: Record<string, string> | null =
          rawTranslations && typeof rawTranslations === 'object'
            ? (rawTranslations as Record<string, string>)
            : null;
        setProfile({
          id: data.id as string,
          name: data.name as string,
          role_label: data.role_label as string,
          role_label_translations,
          ui_lang: data.ui_lang,
          native_lang: data.native_lang,
          timezone: data.timezone as string,
          family_side: (data.family_side as 'tr' | 'bs' | null) ?? null,
        });
      } else {
        setProfile(null);
      }

      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, refreshKey]);

  return {
    profile,
    loading,
    refresh: () => setRefreshKey((k) => k + 1),
  };
}
